'use client';

import { 
  ControlType, 
  CalculatorId, 
  CalculatorType, 
  ControlIds, 
  type CalculatorConfig, 
  type ControlValues,
  type CalculatorControl,
  type SingleColumnSelectControl,
  type MultiColumnSelectControl,
  type TextControl,
  type NumberControl,
  type SelectControl
} from '../../../lib/calculators/calculator-config';
import { TableDataJSON } from '../../../src/types/table-json';
import { LazyTabLabECharts } from '@/components/ui/charts/lazy-tab-lab-echarts';
import type { EChartsOption } from 'echarts';

// Type for radar chart specific control values
export interface RadarChartControlValues extends ControlValues {
  [ControlIds.Category]: string;
  [ControlIds.Values]: string[];
  [ControlIds.Title]: string;
  [ControlIds.AxisMax]: number;
  [ControlIds.ShowArea]: 'true' | 'false';
  [ControlIds.AreaOpacity]: number;
}

// Define the controls for the radar chart
export const radarChartControls = {
  tabs: [
    {
      title: 'Data',
      controls: [
        {
          id: ControlIds.Category,
          type: ControlType.SingleColumnSelect,
          label: 'Category column',
          required: true
        } satisfies SingleColumnSelectControl,
        {
          id: ControlIds.Values,
          type: ControlType.MultiColumnSelect,
          label: 'Value columns',
          required: true,
          numericOnly: true,
          minSelections: 1
        } satisfies MultiColumnSelectControl,
        {
          id: ControlIds.Title,
          type: ControlType.Text,
          label: 'Chart title'
        } satisfies TextControl
      ] as readonly CalculatorControl[]
    },
    {
      title: 'Appearance',
      controls: [
        {
          id: ControlIds.AxisMax,
          type: ControlType.Number,
          label: 'Axis maximum',
          min: 1,
          max: 1000,
          step: 1,
          defaultValue: 100
        } satisfies NumberControl,
        {
          id: ControlIds.ShowArea,
          type: ControlType.Select,
          label: 'Show area',
          options: [
            { label: 'Yes', value: 'true' },
            { label: 'No', value: 'false' }
          ],
          defaultValue: 'true'
        } satisfies SelectControl,
        {
          id: ControlIds.AreaOpacity,
          type: ControlType.Number,
          label: 'Area opacity',
          min: 0,
          max: 100,
          step: 1,
          defaultValue: 30
        } satisfies NumberControl
      ] as readonly CalculatorControl[]
    }
  ]
};

// Function to get radar chart configuration
function getRadarChartConfig({
  categories,
  series,
  title,
  axisMax,
  showArea,
  areaOpacity
}: {
  categories: string[];
  series: { name: string; values: number[] }[];
  title: string;
  axisMax: number;
  showArea: boolean;
  areaOpacity: number;
}): EChartsOption {
  return {
    title: {
      text: title,
      left: 'center'
    },
    tooltip: {
      trigger: 'item'
    },
    legend: {
      top: 28,
      left: 'center'
    },
    radar: {
      indicator: categories.map(name => ({ name, max: axisMax })),
      radius: '60%'
    },
    series: [{
      type: 'radar',
      data: series.map(s => ({
        name: s.name,
        value: s.values,
        areaStyle: showArea ? {
          opacity: areaOpacity / 100
        } : undefined,
        emphasis: {
          focus: 'series',
          areaStyle: showArea ? {
            opacity: (areaOpacity + 20) / 100
          } : undefined
        }
      }))
    }]
  };
}

export const radarChartConfig: CalculatorConfig<CalculatorId.RadarChart> = {
  id: CalculatorId.RadarChart,
  type: CalculatorType.Graph,
  name: 'Radar Chart',
  initialData: TableDataJSON.from({
    columns: [
      { 
        id: 'skill', 
        name: 'Skill', 
        values: ['Technical', 'Communication', 'Leadership', 'Teamwork', 'Problem Solving'] 
      },
      { 
        id: 'employee1', 
        name: 'Employee 1', 
        values: ['85', '75', '65', '90', '80'] 
      },
      { 
        id: 'employee2', 
        name: 'Employee 2', 
        values: ['70', '85', '90', '75', '70'] 
      }
    ]
  }),
  initialControlValues: {
    [ControlIds.Category]: 'skill',
    [ControlIds.Values]: ['employee1', 'employee2'],
    [ControlIds.Title]: 'Employee Skills Assessment',
    [ControlIds.AxisMax]: 100,
    [ControlIds.ShowArea]: 'true',
    [ControlIds.AreaOpacity]: 30
  },
  controls: radarChartControls,
  getInitialConfig: (data: TableDataJSON) => {
    const numericColumns = data.columns.filter(col => 
      col.values.every(val => !isNaN(Number(val)))
    );
    const nonNumericColumns = data.columns.filter(col => 
      !numericColumns.includes(col)
    );

    return {
      [ControlIds.Category]: nonNumericColumns[0]?.id ?? '',
      [ControlIds.Values]: numericColumns.slice(0, 2).map(col => col.id),
      [ControlIds.Title]: 'Radar Chart',
      [ControlIds.AxisMax]: 100,
      [ControlIds.ShowArea]: 'true',
      [ControlIds.AreaOpacity]: 30
    };
  },
  getVisualizationData: ({ data, controls }) => {
    const radarControls = controls as RadarChartControlValues;
    
    if (!data?.columns?.length || !radarControls[ControlIds.Category] || !radarControls[ControlIds.Values]?.length) {
      console.log('Missing data or controls:', { data, controls });
      return null;
    }

    const categoryColumn = data.columns.find(col => col.id === radarControls[ControlIds.Category]);
    if (!categoryColumn) {
      console.log('Category column not found:', radarControls[ControlIds.Category]);
      return null;
    }

    const categories = [...new Set(categoryColumn.values)];
    
    const series = radarControls[ControlIds.Values].map(valueId => {
      const column = data.columns.find(col => col.id === valueId);
      if (!column) {
        console.log('Value column not found:', valueId);
        return undefined;
      }

      return {
        name: column.name,
        values: categories.map((category, index) => Number(column.values[index]) || 0)
      };
    }).filter((s): s is NonNullable<typeof s> => s !== undefined);

    if (series.length === 0) {
      console.log('No valid series data generated');
      return null;
    }

    return getRadarChartConfig({
      categories,
      series,
      title: radarControls[ControlIds.Title],
      axisMax: radarControls[ControlIds.AxisMax],
      showArea: radarControls[ControlIds.ShowArea] === 'true',
      areaOpacity: radarControls[ControlIds.AreaOpacity]
    });
  },
  render: ({ visualizationData }) => 
    visualizationData ? (
      <LazyTabLabECharts option={visualizationData} className="h-full w-full" />
    ) : null
}; 